import { useState, useEffect } from 'react';
import { API_BASE } from '../config';
import { hasEditAccess } from '../permissionConfig';
import { notifyEditBlocked } from '../components/EditAccessToast';

const BASE_URL = API_BASE;

export default function B2BDispatch({ user }) {
    const [section,  setSection]  = useState('packing_slip'); // 'packing_slip' | 'update_po_record'
    const [records,  setRecords]  = useState([]);
    const [loading,  setLoading]  = useState(true);
    const [search,   setSearch]   = useState('');
    const [selected, setSelected] = useState(new Set());
    const [exporting, setExporting] = useState(false);
    const [msg,       setMsg]       = useState(null);
    const [page,      setPage]      = useState(1);
    const PAGE_SIZE = 50;

    useEffect(() => { loadEligible(); }, []);

    async function loadEligible() {
        setLoading(true);
        try {
            const res  = await fetch(`${BASE_URL}/so-packing-slip-download-zip?action=packing_slip_eligible`);
            const data = await res.json();
            setRecords(Array.isArray(data) ? data : []);
        } catch(e) { console.error(e); }
        finally { setLoading(false); }
    }

    const filtered = records.filter(r =>
        !search ||
        r.voucher_no?.toLowerCase().includes(search.toLowerCase()) ||
        r.order_ref_no?.toLowerCase().includes(search.toLowerCase()) ||
        r.buyer?.toLowerCase().includes(search.toLowerCase()) ||
        r.invoice_no?.toLowerCase().includes(search.toLowerCase())
    );
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    useEffect(() => { setPage(1); }, [search, records]);

    function toggle(voucherNo) {
        setSelected(s => {
            const next = new Set(s);
            next.has(voucherNo) ? next.delete(voucherNo) : next.add(voucherNo);
            return next;
        });
    }

    function toggleAll() {
        setSelected(s => s.size === filtered.length
            ? new Set()
            : new Set(filtered.map(r => r.voucher_no)));
    }

    async function exportSelected() {
        if (!hasEditAccess(user, 'po_so_lifecycle', 'b2b_dispatch')) return notifyEditBlocked();
        if (selected.size === 0) return;
        setExporting(true); setMsg(null);
        try {
            const res = await fetch(`${BASE_URL}/so-packing-slip-download-zip`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ voucher_nos: [...selected] })
            });
            if (!res.ok) {
                const text = await res.text().catch(() => 'Export failed');
                setMsg({ type:'error', text });
                return;
            }
            const skippedHeader = res.headers.get('X-Skipped-Vouchers');
            // A single successful voucher now comes back as a plain .xlsx
            // (no zip wrapper) — branch on the actual response instead of
            // always assuming/naming it a zip.
            const isZip = (res.headers.get('content-type') || '').includes('zip');
            const dispositionMatch = (res.headers.get('content-disposition') || '').match(/filename="?([^"]+)"?/);
            const filename = dispositionMatch ? dispositionMatch[1] : (isZip ? 'packing-slips.zip' : 'packing-slip.xlsx');
            const blob = await res.blob();
            const url  = window.URL.createObjectURL(blob);
            const a    = document.createElement('a');
            a.href = url; a.download = filename;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);

            if (skippedHeader) {
                try {
                    const skipped = JSON.parse(atob(skippedHeader));
                    if (skipped.length > 0) {
                        setMsg({ type:'error', text: 'Skipped: ' + skipped.map(s => `${s.voucher_no} (${s.reason})`).join('; ') });
                    } else {
                        setMsg({ type:'success', text: 'Packing slips downloaded' });
                    }
                } catch { setMsg({ type:'success', text: 'Packing slips downloaded' }); }
            } else {
                setMsg({ type:'success', text: 'Packing slips downloaded' });
            }
        } catch(e) { setMsg({ type:'error', text:'Network error' }); }
        finally { setExporting(false); }
    }

    return (
        <div style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            {/* Header bar — GRN-style dark sticky card */}
            <div style={S.headerBar}>
                <h1 style={S.h1}>B2B <span style={{ color: '#C4B5FD' }}>Dispatch</span></h1>
                <div style={{ display: 'flex', gap: 8 }} data-tutorial="b2b-dispatch-tabs">
                    <button onClick={() => setSection('packing_slip')} style={section === 'packing_slip' ? S.tabBtnActive : S.tabBtn}>Packing Slip</button>
                    <button onClick={() => setSection('update_po_record')} style={section === 'update_po_record' ? S.tabBtnActive : S.tabBtn}>Update PO Record</button>
                </div>
                {section === 'packing_slip' && (
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input type="text" style={S.searchInput}
                            placeholder="Search SO, PO, buyer, invoice..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        <button onClick={loadEligible} style={S.ghostBtn}>Refresh</button>
                    </div>
                )}
            </div>

            {section === 'update_po_record' ? (
                <UpdatePoRecordSection user={user} />
            ) : (
            /* Content card */
            <div style={S.card}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
                    <h3 style={S.cardTitle}>Packing Slip Export</h3>
                    <button onClick={exportSelected} disabled={exporting || selected.size === 0} style={S.btnDark}>
                        {exporting ? 'Exporting...' : `Export Selected (${selected.size})`}
                    </button>
                </div>
                {msg && <Alert type={msg.type} text={msg.text} />}
                {loading ? <div style={S.loadingStyle}>Loading...</div> : (
                    <div style={{ overflowX:'auto' }}>
                        <table style={S.tblStyle}>
                            <thead>
                                <tr>
                                    <th style={S.thStyle}>
                                        <input type="checkbox"
                                            checked={filtered.length > 0 && selected.size === filtered.length}
                                            onChange={toggleAll} />
                                    </th>
                                    {['SO','PO Ref','Buyer','Invoice No.','Boxes','Date'].map(h => (
                                        <th key={h} style={S.thStyle}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr><td colSpan={7} style={S.emptyStyle}>No packed, box-mapped SOs available</td></tr>
                                ) : paged.map((r,i) => (
                                    <tr key={r.voucher_no} style={{ background: i%2===0?'#fff':'#FAFAFC' }}>
                                        <td style={S.tdStyle}>
                                            <input type="checkbox"
                                                checked={selected.has(r.voucher_no)}
                                                onChange={() => toggle(r.voucher_no)} />
                                        </td>
                                        <td style={{ ...S.tdStyle, fontWeight:700 }}>{r.voucher_no}</td>
                                        <td style={S.tdStyle}>{r.order_ref_no || '—'}</td>
                                        <td style={{ ...S.tdStyle, maxWidth:'160px', whiteSpace:'normal' }}>{r.buyer}</td>
                                        <td style={S.tdStyle}>{r.invoice_no || '—'}</td>
                                        <td style={{ ...S.tdStyle, fontWeight:700 }}>{r.box_count}</td>
                                        <td style={S.tdStyle}>{r.so_date ? String(r.so_date).slice(0,10) : '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {filtered.length > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 20 }}>
                                <button style={S.ghostBtnLight} onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
                                    ← Prev
                                </button>
                                <span style={{ fontSize: 13, color: '#6B6580' }}>Page {page} of {totalPages}</span>
                                <button style={S.ghostBtnLight} onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                                    Next →
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
            )}
        </div>
    );
}

function Alert({ type, text }) {
    const colors = {
        error:   { bg:'#FEE2E2', color:'#991B1B' },
        success: { bg:'#EDE9FE', color:'#5B21B6' },
        info:    { bg:'#EFF6FF', color:'#1D4ED8' },
    };
    const c = colors[type] || colors.info;
    return (
        <div style={{ padding:'10px 16px', background:c.bg, color:c.color, borderRadius:'8px', fontSize:'14px', fontWeight:600, marginBottom:'14px' }}>
            {text}
        </div>
    );
}

// ── Styles — visual conventions borrowed from GRNManagement.jsx (dark
// sticky header bar, Space Grotesk, generous radii/shadows) but as plain
// static light-mode values, no CSS-variable theme/dark-mode plumbing.
const S = {
    headerBar: {
        position: 'sticky', top: 0, zIndex: 20, display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
        background: '#0B0A12', borderRadius: 16, border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 4px 16px rgba(0,0,0,0.18)', padding: '14px 20px', marginBottom: 20,
    },
    h1: { fontFamily: "'Space Grotesk', sans-serif", fontWeight: 300, fontSize: 22, margin: 0, color: '#F3EFFA', letterSpacing: '-0.01em', whiteSpace: 'nowrap' },
    searchInput: { padding: '9px 14px', borderRadius: 999, border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(255,255,255,0.06)', color: '#F3EFFA', fontSize: '13px', outline: 'none', width: '260px' },
    ghostBtn: { padding: '9px 16px', borderRadius: 999, border: '1px solid rgba(255,255,255,0.14)', background: 'transparent', color: '#F3EFFA', fontSize: '13px', fontWeight: 600, cursor: 'pointer' },
    ghostBtnLight: { padding: '8px 16px', borderRadius: 999, border: '1px solid #DDD6EE', background: '#fff', color: '#2D2A38', fontSize: '13px', fontWeight: 600, cursor: 'pointer' },
    card: { background: '#fff', borderRadius: 14, border: '1px solid #ECE8F5', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', padding: '20px 24px' },
    cardTitle: { fontSize: '16px', fontWeight: 700, color: '#0F0A1F', margin: 0 },
    btnDark: { padding: '10px 22px', background: 'linear-gradient(135deg,#1A1A1A,#3D3D3D)', color: '#fff', border: 'none', borderRadius: 10, fontSize: '13.5px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.18)' },
    tblStyle: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
    thStyle: { background: '#0B0A12', color: '#F3EFFA', padding: '9px 12px', textAlign: 'left', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' },
    tdStyle: { padding: '8px 12px', borderBottom: '1px solid #F1EEF8', color: '#2D2A38', whiteSpace: 'nowrap' },
    emptyStyle: { padding: '30px', textAlign: 'center', color: '#9A93A8' },
    loadingStyle: { padding: '20px', textAlign: 'center', color: '#9A93A8', fontSize: '14px' },
    tabBtn: { padding: '9px 18px', borderRadius: 999, border: '1px solid rgba(255,255,255,0.14)', background: 'transparent', color: '#B9AEDD', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: "'Space Grotesk', sans-serif" },
    tabBtnActive: { padding: '9px 18px', borderRadius: 999, border: '1px solid #6E58A6', background: '#6E58A6', color: '#fff', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: "'Space Grotesk', sans-serif", boxShadow: '0 2px 8px rgba(110,88,166,0.45)' },
};

// ── "Update PO Record" — manual AWB/status/POD entry for shipments that
// never touch Shiprocket. Writes into the same shiprocket_mis_shipments
// table (via b2b-po-record.js), so results show up automatically in PO
// Tracker / GRN Management with no extra plumbing. Purple theming per spec,
// values lifted from GRNManagement.jsx's --grn-purple tokens.
const PURPLE = '#6E58A6';
const PURPLE_DARK = '#4A3777';
const PURPLE_TINT = '#EEE9F8';

const STATUS_OPTIONS = [
    'Picked Up', 'In Transit', 'Reached At Destination', 'Out For Delivery',
    'Delivered', 'RTO In Transit', 'RTO Delivered', 'Undelivered', 'Cancelled', 'Archived',
];

function UpdatePoRecordSection({ user }) {
    const [query, setQuery] = useState('');
    const [searching, setSearching] = useState(false);
    const [result, setResult] = useState(null);
    const [drafts, setDrafts] = useState({});
    const [savingKey, setSavingKey] = useState(null);
    const [uploadingKey, setUploadingKey] = useState(null);
    const [msg, setMsg] = useState(null);

    async function doSearch() {
        const q = query.trim();
        if (!q) return;
        setSearching(true); setMsg(null); setResult(null);
        try {
            const res = await fetch(`${BASE_URL}/b2b-po-record?action=search&q=${encodeURIComponent(q)}`);
            const data = await res.json();
            setResult(data);
            if (data.found) {
                const nextDrafts = {};
                data.invoices.forEach(inv => {
                    nextDrafts[inv.invoice_no] = {
                        waybill_no: inv.shipment?.waybill_no || '',
                        status: inv.shipment?.status || '',
                    };
                });
                setDrafts(nextDrafts);
            }
        } catch { setMsg({ type: 'error', text: 'Network error' }); }
        finally { setSearching(false); }
    }

    function updateDraft(invoiceNo, field, value) {
        setDrafts(d => ({ ...d, [invoiceNo]: { ...d[invoiceNo], [field]: value } }));
    }

    async function saveRecord(invoiceNo) {
        if (!hasEditAccess(user, 'po_so_lifecycle', 'b2b_update_po_record')) return notifyEditBlocked();
        const draft = drafts[invoiceNo] || {};
        setSavingKey(invoiceNo); setMsg(null);
        try {
            const res = await fetch(`${BASE_URL}/b2b-po-record?action=save`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    invoice_no: invoiceNo,
                    waybill_no: draft.waybill_no || null,
                    status: draft.status || null,
                    updated_by: user?.username || 'unknown',
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok && data.success) {
                setMsg({ type: 'success', text: `Saved AWB/status for ${invoiceNo}` });
                await doSearch();
            } else {
                setMsg({ type: 'error', text: data.error || 'Failed to save' });
            }
        } catch { setMsg({ type: 'error', text: 'Network error' }); }
        finally { setSavingKey(null); }
    }

    async function uploadPod(invoiceNo, file) {
        if (!hasEditAccess(user, 'po_so_lifecycle', 'b2b_update_po_record')) return notifyEditBlocked();
        if (!file) return;
        const draft = drafts[invoiceNo] || {};
        setUploadingKey(invoiceNo); setMsg(null);
        try {
            const formData = new FormData();
            formData.append('invoice_no', invoiceNo);
            if (draft.waybill_no) formData.append('waybill_no', draft.waybill_no);
            formData.append('updated_by', user?.username || 'unknown');
            formData.append('pod', file);
            const res = await fetch(`${BASE_URL}/b2b-po-record?action=upload_pod`, { method: 'POST', body: formData });
            const data = await res.json().catch(() => ({}));
            if (res.ok && data.success) {
                setMsg({ type: 'success', text: `POD uploaded for ${invoiceNo}` });
                await doSearch();
            } else {
                setMsg({ type: 'error', text: data.error || 'Failed to upload POD' });
            }
        } catch { setMsg({ type: 'error', text: 'Network error' }); }
        finally { setUploadingKey(null); }
    }

    return (
        <div style={S.card}>
            <h3 style={S.cardTitle}>Update PO Record</h3>
            <p style={PS.tagline}>Hey, Update details of your Purchase Order like AWB number, Tracking status and Upload your POD here.</p>

            <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
                <input
                    type="text"
                    placeholder="Search PO number or Invoice number..."
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') doSearch(); }}
                    style={PS.searchInputLight}
                />
                <button onClick={doSearch} disabled={searching} style={PS.btnPurple}>
                    {searching ? 'Searching…' : 'Search'}
                </button>
            </div>

            {msg && <Alert type={msg.type} text={msg.text} />}

            {result && !result.found && (
                <div style={S.emptyStyle}>No PO or invoice found matching "{query}".</div>
            )}

            {result?.found && (
                <div>
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ fontWeight: 700, fontSize: 15, color: '#2D2A38' }}>{result.po_number}</div>
                        <div style={{ fontSize: 12.5, color: '#6B6580' }}>
                            {result.party_name || '—'} · PO Date {result.po_date ? String(result.po_date).slice(0, 10) : '—'}
                        </div>
                    </div>

                    {result.invoices.length === 0 && (
                        <div style={S.emptyStyle}>No invoice found for this PO yet.</div>
                    )}

                    {result.invoices.map(inv => {
                        const draft = drafts[inv.invoice_no] || {};
                        const shipment = inv.shipment;
                        return (
                            <div key={inv.invoice_no} style={PS.invoiceCard}>
                                <div style={{ fontWeight: 700, fontSize: 13.5, color: PURPLE_DARK, marginBottom: 10 }}>{inv.invoice_no}</div>

                                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        <span style={PS.label}>AWB Number</span>
                                        <input
                                            type="text"
                                            value={draft.waybill_no || ''}
                                            onChange={e => updateDraft(inv.invoice_no, 'waybill_no', e.target.value)}
                                            placeholder="Enter AWB number"
                                            style={PS.fieldInput}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        <span style={PS.label}>Status (optional)</span>
                                        <select
                                            value={draft.status || ''}
                                            onChange={e => updateDraft(inv.invoice_no, 'status', e.target.value)}
                                            style={PS.fieldInput}
                                        >
                                            <option value="">— Select status —</option>
                                            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </div>
                                    <button onClick={() => saveRecord(inv.invoice_no)} disabled={savingKey === inv.invoice_no} style={PS.btnPurple}>
                                        {savingKey === inv.invoice_no ? 'Saving…' : 'Save'}
                                    </button>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14 }}>
                                    <span style={PS.label}>POD</span>
                                    {shipment?.pod_blob_name && <span style={PS.podChip}>POD on file</span>}
                                    <label style={PS.uploadBtn}>
                                        {uploadingKey === inv.invoice_no ? 'Uploading…' : 'Choose file & Upload'}
                                        <input
                                            type="file"
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            style={{ display: 'none' }}
                                            disabled={uploadingKey === inv.invoice_no}
                                            onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) uploadPod(inv.invoice_no, f); }}
                                        />
                                    </label>
                                </div>

                                {(shipment?.created_at || shipment?.updated_by) && (
                                    <div style={{ marginTop: 10, fontSize: 11.5, color: '#9A93A8' }}>
                                        {shipment.created_at && <>Created {String(shipment.created_at).slice(0, 10)}</>}
                                        {shipment.created_at && shipment.updated_by && ' · '}
                                        {shipment.updated_by && <>Last updated by {shipment.updated_by}</>}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

const PS = {
    tagline: { fontSize: 13, color: '#6B6580', margin: '4px 0 18px' },
    searchInputLight: { flex: 1, padding: '10px 16px', borderRadius: 10, border: `1px solid ${PURPLE}55`, fontSize: '13.5px', outline: 'none' },
    btnPurple: { padding: '10px 20px', background: PURPLE, color: '#fff', border: 'none', borderRadius: 10, fontSize: '13px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 6px rgba(110,88,166,0.35)' },
    invoiceCard: { border: `1px solid ${PURPLE}33`, background: PURPLE_TINT, borderRadius: 12, padding: '16px 18px', marginBottom: 14 },
    label: { fontSize: '11px', fontWeight: 700, color: PURPLE_DARK, textTransform: 'uppercase', letterSpacing: '0.04em' },
    fieldInput: { padding: '9px 12px', borderRadius: 8, border: `1px solid ${PURPLE}55`, fontSize: '13px', minWidth: 180, outline: 'none' },
    podChip: { padding: '3px 10px', borderRadius: 20, background: '#E4F1E7', color: '#2C5138', fontSize: '11.5px', fontWeight: 600 },
    uploadBtn: { display: 'inline-flex', alignItems: 'center', padding: '8px 16px', borderRadius: 8, border: `1px solid ${PURPLE}`, color: PURPLE, background: '#fff', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' },
};
